import { useState, useEffect, useCallback } from 'react';
import { X, Trash2, Calendar, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import type { ProjectWithTasks } from '../../types/projects';
import { AGENTS, DEPARTMENTS, getAgentColor } from '../../utils/agents';
import { useProjectStore } from '../../stores/projectStore';
import Spinner from '../common/Spinner';

const STATUS_OPTIONS = [
  { key: 'active' as const, label: 'Active' },
  { key: 'completed' as const, label: 'Completed' },
  { key: 'archived' as const, label: 'Archived' },
];

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'text-error',
  high: 'text-warning',
  medium: 'text-text-secondary',
  low: 'text-text-muted',
};

const TASK_STATUS_LABELS: Record<string, string> = {
  backlog: 'Backlog',
  in_progress: 'In Progress',
  completed: 'Completed',
};

interface ProjectDetailProps {
  projectId: number;
  onClose: () => void;
}

export default function ProjectDetail({ projectId, onClose }: ProjectDetailProps) {
  const fetchProject = useProjectStore((s) => s.fetchProject);
  const selectedProject = useProjectStore((s) => s.selectedProject);
  const detailLoading = useProjectStore((s) => s.detailLoading);
  const updateProject = useProjectStore((s) => s.updateProject);
  const deleteProject = useProjectStore((s) => s.deleteProject);

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('active');
  const [department, setDepartment] = useState('');
  const [leadAgent, setLeadAgent] = useState('');
  const [startDate, setStartDate] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    fetchProject(projectId);
  }, [fetchProject, projectId]);

  useEffect(() => {
    if (selectedProject) {
      setName(selectedProject.name);
      setDescription(selectedProject.description || '');
      setStatus(selectedProject.status);
      setDepartment(selectedProject.department || '');
      setLeadAgent(selectedProject.lead_agent || '');
      setStartDate(selectedProject.start_date || '');
      setTargetDate(selectedProject.target_date || '');
    }
  }, [selectedProject]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') handleClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  function handleAgentChange(slug: string) {
    setLeadAgent(slug);
    if (slug) {
      const agent = AGENTS.find((a) => a.slug === slug);
      if (agent) {
        setDepartment(agent.department);
      }
    }
  }

  async function handleSave() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await updateProject(projectId, {
        name: name.trim(),
        description: description.trim() || null,
        status: status as 'active' | 'completed' | 'archived',
        department: department || null,
        lead_agent: leadAgent || null,
        start_date: startDate || null,
        target_date: targetDate || null,
      });
      await fetchProject(projectId);
      setEditing(false);
    } catch (err) {
      console.error('Failed to update project:', err);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    try {
      await deleteProject(projectId);
      onClose();
    } catch (err) {
      console.error('Failed to delete project:', err);
    }
  }

  function formatTimestamp(ts: string | null): string {
    if (!ts) return '-';
    return format(new Date(ts), 'MMM d, yyyy');
  }

  // Group tasks by status
  function groupTasks(project: ProjectWithTasks) {
    const groups: Record<string, typeof project.tasks> = {
      in_progress: [],
      backlog: [],
      completed: [],
    };
    for (const task of project.tasks) {
      if (groups[task.status]) {
        groups[task.status].push(task);
      } else {
        groups[task.status] = [task];
      }
    }
    return groups;
  }

  if (detailLoading || !selectedProject) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/60" onClick={handleClose} />
        <div className="relative bg-bg-secondary border border-border-subtle rounded-xl p-6 w-full max-w-2xl mx-4">
          <div className="flex items-center justify-center py-12">
            <Spinner size="md" />
          </div>
        </div>
      </div>
    );
  }

  const project = selectedProject;
  const agent = project.lead_agent ? AGENTS.find((a) => a.slug === project.lead_agent) : null;
  const dept = project.department ? DEPARTMENTS.find((d) => d.id === project.department) : null;
  const taskGroups = groupTasks(project);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />
      <div className="relative bg-bg-secondary border border-border-subtle rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-text-primary">Project Details</h2>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-bg-tertiary text-text-muted hover:text-text-primary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {editing ? (
          /* Edit mode */
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple"
              />
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple resize-none"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-2">Status</label>
              <div className="flex gap-2">
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setStatus(opt.key)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      status === opt.key
                        ? 'bg-accent-purple text-white'
                        : 'bg-bg-tertiary text-text-muted hover:text-text-secondary'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-text-secondary mb-1">Department</label>
                <select
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
                >
                  <option value="">None</option>
                  {DEPARTMENTS.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-text-secondary mb-1">Lead Agent</label>
                <select
                  value={leadAgent}
                  onChange={(e) => handleAgentChange(e.target.value)}
                  className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
                >
                  <option value="">None</option>
                  {AGENTS.map((a) => (
                    <option key={a.slug} value={a.slug}>
                      {a.name} — {a.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-text-secondary mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple"
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">Target Date</label>
                <input
                  type="date"
                  value={targetDate}
                  onChange={(e) => setTargetDate(e.target.value)}
                  className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-2">
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="text-text-muted hover:text-text-secondary text-sm transition-colors px-3 py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !name.trim()}
                className="bg-accent-purple text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-accent-purple/80 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        ) : (
          /* View mode */
          <div className="flex flex-col gap-5">
            {/* Project info header */}
            <div className="flex flex-col gap-2">
              <div className="flex items-start justify-between">
                <h3 className="text-base font-semibold text-text-primary">{project.name}</h3>
                <button
                  onClick={() => setEditing(true)}
                  className="text-xs text-accent-purple-light hover:text-accent-purple transition-colors px-2 py-1"
                >
                  Edit
                </button>
              </div>
              {project.description ? (
                <p className="text-sm text-text-secondary">{project.description}</p>
              ) : null}
            </div>

            {/* Meta row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <span className="text-xs text-text-muted block mb-0.5">Status</span>
                <span className="text-sm text-text-primary capitalize">{project.status}</span>
              </div>
              <div>
                <span className="text-xs text-text-muted block mb-0.5">Lead</span>
                {agent ? (
                  <div className="flex items-center gap-1.5">
                    <div className={`h-4 w-4 rounded-full ${getAgentColor(agent.department)} flex items-center justify-center`}>
                      <span className="text-[8px] text-white font-bold">{agent.name.charAt(0)}</span>
                    </div>
                    <span className="text-sm text-text-primary">{agent.shortName}</span>
                  </div>
                ) : (
                  <span className="text-sm text-text-muted">-</span>
                )}
              </div>
              <div>
                <span className="text-xs text-text-muted block mb-0.5">Department</span>
                <span className="text-sm text-text-primary">{dept ? dept.name : '-'}</span>
              </div>
              <div>
                <span className="text-xs text-text-muted block mb-0.5">Target</span>
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3 text-text-muted" />
                  <span className="text-sm text-text-primary">{formatTimestamp(project.target_date)}</span>
                </div>
              </div>
            </div>

            {/* Dates row */}
            <div className="flex gap-4 text-xs border-t border-border-subtle pt-3">
              <div>
                <span className="text-text-muted">Start:</span>{' '}
                <span className="text-text-secondary">{formatTimestamp(project.start_date)}</span>
              </div>
              <div>
                <span className="text-text-muted">Created:</span>{' '}
                <span className="text-text-secondary">{formatTimestamp(project.created_at)}</span>
              </div>
              <div>
                <span className="text-text-muted">Updated:</span>{' '}
                <span className="text-text-secondary">{formatTimestamp(project.updated_at)}</span>
              </div>
            </div>

            {/* Task list */}
            <div className="border-t border-border-subtle pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-text-primary">
                  Tasks ({project.tasks.length})
                </h4>
                <span className="text-xs text-text-muted flex items-center gap-1">
                  <ExternalLink className="h-3 w-3" />
                  Manage in Tasks page
                </span>
              </div>

              {project.tasks.length === 0 ? (
                <p className="text-sm text-text-muted text-center py-4">
                  No tasks linked to this project yet.
                </p>
              ) : (
                <div className="flex flex-col gap-4">
                  {(['in_progress', 'backlog', 'completed'] as const).map((statusKey) => {
                    const tasks = taskGroups[statusKey];
                    if (!tasks || tasks.length === 0) return null;

                    return (
                      <div key={statusKey}>
                        <h5 className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">
                          {TASK_STATUS_LABELS[statusKey] || statusKey} ({tasks.length})
                        </h5>
                        <div className="flex flex-col gap-1">
                          {tasks.map((task) => {
                            const taskAgent = task.assigned_agent
                              ? AGENTS.find((a) => a.slug === task.assigned_agent)
                              : null;

                            return (
                              <div
                                key={task.id}
                                className="flex items-center gap-2 px-3 py-2 rounded-md bg-bg-tertiary"
                              >
                                <span className="text-sm text-text-primary flex-1 truncate">
                                  {task.title}
                                </span>
                                <span className={`text-xs font-medium capitalize ${PRIORITY_COLORS[task.priority] || 'text-text-muted'}`}>
                                  {task.priority}
                                </span>
                                {taskAgent ? (
                                  <span className="text-xs text-text-muted">{taskAgent.shortName}</span>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="flex justify-between items-center border-t border-border-subtle pt-3">
              <button
                onClick={handleDelete}
                className={`flex items-center gap-1.5 text-sm transition-colors px-3 py-2 rounded-md ${
                  confirmDelete
                    ? 'bg-error/15 text-error'
                    : 'text-text-muted hover:text-error'
                }`}
              >
                <Trash2 className="h-4 w-4" />
                {confirmDelete ? 'Confirm Delete' : 'Delete'}
              </button>

              <button
                onClick={handleClose}
                className="text-text-muted hover:text-text-secondary text-sm transition-colors px-3 py-2"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
